import React, { createContext, useEffect, useState } from 'react'
import { CONNECTION_STATE_CHANGE, ConnectionState, events } from 'aws-amplify/data'
import useAuth from '../hooks/useAuth'
import { Hub } from 'aws-amplify/utils'

export interface UpdatesSubscriptionState {
    clearCounter: () => void
    error: null | Error
    isConnected: boolean
    isSubscribed: boolean
    subscribToUpdates: () => Promise<void>
    updatesCount: number
}

export const UpdatesSubscriptionContext = createContext<UpdatesSubscriptionState>({
    clearCounter: () => {
        throw new Error('Not initiated yet')
    },
    error: null,
    isConnected: false,
    isSubscribed: false,
    subscribToUpdates: () => Promise.reject(new Error('Not initiated yet.')),
    updatesCount: 0,
})

export const UpdatesSubscriptionsProvider = ({ children }: { children: React.ReactNode }) => {
    const { user } = useAuth()
    const [isConnected, setIsConnected] = useState(false)
    const [error, setError] = useState<UpdatesSubscriptionState['error']>(null)
    const [updatesCount, setUpdatesCount] = useState(0)
    const [isSubscribed, setIsSubscribed] = useState(true)
    const userGroups = user?.groups || null

    const subscribToUpdates = async () => {
        const permission = await Notification.requestPermission()

        setIsSubscribed(permission === 'granted')
    }

    const clearCounter = () => setUpdatesCount(0)

    useEffect(() => {
        void (async () => {
            if (!userGroups?.includes('superadmin') || !isSubscribed) return

            Hub.listen('api', (data: { payload: { event: string; data: { connectionState?: ConnectionState } } }) => {
                const { payload } = data

                if (payload.event === CONNECTION_STATE_CHANGE) {
                    const connectionState = payload.data.connectionState

                    if (connectionState === ConnectionState.Connected) {
                        setIsConnected(true)
                    }

                    if (connectionState === ConnectionState.Disconnected) {
                        setIsConnected(false)
                    }
                }
            })

            const channel = await events.connect('/default/press-releases')
            channel.subscribe({
                next: () => {
                    setUpdatesCount((state) => {
                        const newCount = state + 1

                        return newCount
                    })
                },
                error: (err) => {
                    setError(err)
                    console.error('error', err)
                },
            })
        })()
    }, [isSubscribed, userGroups])

    return (
        <UpdatesSubscriptionContext.Provider
            value={{
                clearCounter,
                error,
                isConnected,
                isSubscribed,
                subscribToUpdates,
                updatesCount,
            }}
        >
            {children}
        </UpdatesSubscriptionContext.Provider>
    )
}
